import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { requestsAPI } from '../../lib/api';
import { getServiceName, formatDate, formatDateTime, REQUEST_STATUS, formatPrice } from '../../lib/constants';
import { CalendarBlank, ChatCircle, Clock, MapPin, Dog, CheckCircle, XCircle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function ClientRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const fetchRequests = useCallback(async () => {
    try {
      const data = await requestsAPI.getRequests();
      setRequests(data);
    } catch (error) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleCancelRequest = async (requestId) => {
    try {
      await requestsAPI.updateStatus(requestId, 'cancelled');
      toast.success('Pedido cancelado');
      fetchRequests();
    } catch (error) {
      toast.error('Erro ao cancelar pedido');
    }
  };

  const filteredRequests = requests.filter(req => {
    if (activeTab === 'all') return true;
    return req.status === activeTab;
  });

  const RequestCard = ({ request }) => {
    const provider = request.provider || {};
    const providerUser = provider.user || {};
    const status = REQUEST_STATUS[request.status] || { label: request.status, color: 'bg-gray-100' };
    const pets = request.pets_details || [];

    return (
      <Card className="provider-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              {providerUser.picture ? (
                <Avatar>
                  <AvatarImage src={providerUser.picture} />
                  <AvatarFallback>{providerUser.name?.charAt(0)}</AvatarFallback>
                </Avatar>
              ) : (
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Dog className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
              <div>
                <h3 className="font-semibold">
                  {providerUser.name || 'A aguardar prestador'}
                </h3>
                <Badge className={`${status.color} text-xs`}>
                  {status.label}
                </Badge>
              </div>
            </div>
            {request.price && (
              <span className="font-bold text-primary">{formatPrice(request.price)}</span>
            )}
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <p className="flex items-center gap-2">
              <Dog className="w-4 h-4" />
              {getServiceName(request.service_type)}
            </p>
            <p className="flex items-center gap-2">
              <CalendarBlank className="w-4 h-4" />
              {formatDateTime(request.start_date)}
              {request.end_date && ` - ${formatDate(request.end_date)}`}
            </p>
            <p className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {request.address}
            </p>
            {pets.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs">Animais:</span>
                {pets.map(pet => (
                  <Badge key={pet.pet_id} variant="outline" className="text-xs">
                    {pet.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 mt-4">
            {request.status === 'accepted' && (
              <Link to={`/client/chat/${request.request_id}`} className="flex-1">
                <Button variant="outline" className="w-full rounded-full" data-testid={`chat-btn-${request.request_id}`}>
                  <ChatCircle className="w-4 h-4 mr-2" />
                  Chat
                </Button>
              </Link>
            )}
            {request.status === 'pending' && (
              <Button 
                variant="outline" 
                className="flex-1 rounded-full text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => handleCancelRequest(request.request_id)}
                data-testid={`cancel-btn-${request.request_id}`}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            )}
            {request.status === 'completed' && !request.reviewed && (
              <Link to={`/client/review/${request.request_id}`} className="flex-1">
                <Button className="w-full rounded-full" data-testid={`review-btn-${request.request_id}`}>
                  Avaliar
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold font-outfit mb-6">Meus Pedidos</h1>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full mb-6">
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="pending">Pendentes</TabsTrigger>
              <TabsTrigger value="accepted">Aceites</TabsTrigger>
              <TabsTrigger value="completed">Concluídos</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {(() => {
                if (loading) {
                  return (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="h-48 animate-pulse bg-muted" />
                      ))}
                    </div>
                  );
                }
                if (filteredRequests.length === 0) {
                  return (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Clock weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-semibold mb-2">Sem pedidos</h3>
                        <p className="text-muted-foreground text-sm mb-4">
                          {activeTab === 'all' 
                            ? 'Ainda não fez nenhum pedido de serviço'
                            : `Não tem pedidos ${REQUEST_STATUS[activeTab]?.label.toLowerCase() || activeTab}`}
                        </p>
                        <Link to="/client/providers">
                          <Button className="rounded-full">
                            Encontrar Prestadores
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  );
                }
                return (
                  <div className="space-y-4">
                    {filteredRequests.map((request) => (
                      <RequestCard key={request.request_id} request={request} />
                    ))}
                  </div>
                );
              })()}
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
